import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Formik } from 'formik';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';

const logo_mobile = 'https://bu-page-assets.s3.ap-northeast-1.amazonaws.com/static-assets/others/Benadir University Logo.png';

const AttendanceLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, error: authError, clearError } = useAttendanceAuth();
  const [error, setError] = useState(null);

  const validationSchema = yup.object({
    email: yup
      .string('Enter your email')
      .email('Enter a valid email')
      .required('Email is required'),
    password: yup
      .string('Enter your password')
      .min(5, 'Password should be of minimum 5 characters length')
      .required('Password is required')
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on roles
      if (user.is_tutor && user.is_faculty_admin) {
        navigate('/attendance/dashboard');
      } else if (user.is_faculty_admin) {
        navigate('/attendance/faculty-admin/dashboard');
      } else if (user.is_tutor) {
        navigate('/attendance/tutor/dashboard');
      } else {
        navigate('/attendance/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
    setError(null);
  }, [clearError]);

  const handleSubmit = async (values, actions) => {
    try {
      actions.setSubmitting(true);
      setError(null);
      clearError();

      console.log('Attempting login with email:', values.email); // Debug log
      const result = await login(values.email, values.password);
      console.log('Login result:', result); // Debug log

      if (result && result.success) {
        // Navigation will happen in useEffect based on roles
        const user = result.user;
        if (user.is_tutor && user.is_faculty_admin) {
          navigate('/attendance/dashboard');
        } else if (user.is_faculty_admin) {
          navigate('/attendance/faculty-admin/dashboard');
        } else if (user.is_tutor) {
          navigate('/attendance/tutor/dashboard');
        } else {
          setError('No valid role assigned. Contact administrator.');
        }
      } else {
        // Prefer message over error type for better user experience
        // According to guide Error #29: Server Error includes both error and message
        const errorMsg = result?.message || result?.error || 'Login failed';
        console.error('Login failed:', errorMsg, 'Full result:', result); // Debug log
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Login error:', err); // Debug log
      const errorMsg = err.message || 'Network error. Please check your connection.';
      setError(errorMsg);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const displayError = error || authError;

  return (
    <div className='bg-blue-500 min-h-screen w-full flex justify-center items-center p-4 sm:p-6 md:p-8'>
      <div className='w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-md'>
        <div className="card overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl bg-white shadow-2xl">
          <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10">
            <a href="/" className="flex justify-center mb-6 sm:mb-8">
              <img className="h-8 sm:h-10 md:h-12" src={logo_mobile} alt="Benadir University Logo" />
            </a>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-2 sm:mb-3 text-gray-800">
              Attendance Management
            </h2>
            <p className="text-center text-gray-600 mb-6 sm:mb-8 text-xs sm:text-sm md:text-base">
              Sign in to manage attendance
            </p>

            {displayError && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="text-red-700 text-xs sm:text-sm font-medium whitespace-pre-line">{displayError}</div>
              </div>
            )}

            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className='space-y-4 sm:space-y-5'>
                  <div>
                    <label className="mb-2 block text-sm sm:text-base font-medium text-gray-700" htmlFor="email">
                      Email Address
                    </label>
                    <input
                      placeholder="Enter your email address"
                      type="email"
                      name="email"
                      id="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      className='form-input w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition'
                      disabled={isSubmitting}
                    />
                    {errors.email && touched.email && (
                      <p className="text-red-600 text-xs sm:text-sm mt-1.5 font-medium">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm sm:text-base font-medium text-gray-700" htmlFor="password">
                        Password
                      </label>
                    </div>
                    <input
                      placeholder="Enter your password"
                      type="password"
                      name="password"
                      id="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      className='form-input w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition'
                      disabled={isSubmitting}
                    />
                    {errors.password && touched.password && (
                      <p className="text-red-600 text-xs sm:text-sm mt-1.5 font-medium">{errors.password}</p>
                    )}
                  </div>

                  <div className='pt-2'>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className='btn w-full text-sm sm:text-base text-white bg-blue-600 hover:bg-blue-700 py-3 sm:py-3.5 px-4 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg'
                      aria-label="Sign in to attendance system"
                    >
                      {isSubmitting ? 'Logging in...' : 'Sign In'}
                    </button>
                  </div>
                </form>
              )}
            </Formik>

            <div className="text-center mt-6 sm:mt-8">
              <a href="/" className="text-blue-600 text-xs sm:text-sm font-medium hover:text-blue-700 hover:underline transition">
                Back to main site
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceLogin;

